import { type PropsWithChildren, type ReactElement, useMemo } from 'react';

import { OverlayProvider } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ChartSeriesActionContext } from '../../../core/components/series-action/ChartSeriesActionContext.js';
import { ColorRuleContext } from '../../../core/context/ColorRuleContext.js';
import { FormatterContext } from '../../shared/context/FormatterContext.js';
import { MeterBarChartLoadingContext } from '../../shared/context/MeterBarChartLoadingContext.js';
import { MeterBarToolbarConfigContext } from '../../shared/context/MeterBarToolbarConfigContext.js';
import {
  MeterBarChartInternalConfigContext,
  MeterBarChartInternalConfigContextProps,
} from '../context/MeterBarChartInternalConfigContext.js';
import type { MeterBarChartInternalConfig } from '../types/meter-bar-chart-internal.js';
import { type MeterBarChartProps } from '../types/meter-bar-chart.js';

interface MeterBarChartProvidersProps {
  config: MeterBarChartInternalConfig;
  seriesActions?: MeterBarChartProps['seriesActions'];
}
export const MeterBarChartProviders = ({
  config,
  seriesActions,
  children,
}: PropsWithChildren<MeterBarChartProvidersProps>) => {
  const contextSeriesActionsValue = useMemo(
    () => ({
      chartSeriesActions: seriesActions as (
        series: unknown,
        datapoint?: unknown,
      ) => ReactElement<PropsWithChildren>,
    }),
    [seriesActions],
  );

  const internalConfigValue: MeterBarChartInternalConfigContextProps = useMemo(
    () => ({
      min: config.min,
      max: config.max,
      tooltip: config.tooltip,
      label: config.labelSlot,
      thresholds: config.thresholds,
      thresholdsIndicators: config.thresholdsIndicators,
      showThresholdIndicators: config.showThresholdIndicators,
    }),
    [config],
  );

  return (
    <ColorRuleContext.Provider value={config.colorRules}>
      <FormatterContext.Provider
        value={{ formatter: config.formatter, unit: config.unit }}
      >
        <MeterBarChartLoadingContext.Provider value={config.loading}>
          <ChartSeriesActionContext.Provider value={contextSeriesActionsValue}>
            <MeterBarChartInternalConfigContext.Provider
              value={internalConfigValue}
            >
              <MeterBarToolbarConfigContext.Provider value={config.toolbar}>
                <OverlayProvider>
                  <TooltipProvider>{children}</TooltipProvider>
                </OverlayProvider>
              </MeterBarToolbarConfigContext.Provider>
            </MeterBarChartInternalConfigContext.Provider>
          </ChartSeriesActionContext.Provider>
        </MeterBarChartLoadingContext.Provider>
      </FormatterContext.Provider>
    </ColorRuleContext.Provider>
  );
};
